import { useEffect, useState } from "react";
import type { Block, BlockType, EditProps, RenderProps } from "./types";
import { InlineRichText } from "./inline-text";
import { MediaPicker } from "./MediaPicker";
import { escapeHtml, makeBlock, newBlockId } from "./utils";
import { useBlockTypeContext } from "./context";
import { ChantEmbed } from "../chant/ChantEmbed";
import { ChantPicker } from "./ChantPicker";
import { SankalpaModule } from "../sankalpa/SankalpaModule";
import { isEmptySelection, type ChantSelection } from "@vedaunion/shared";

/* =============================================================================
 * Shared option types
 * ========================================================================== */

type Align = "left" | "center" | "right";

function AlignPicker({ value, onChange }: { value: Align; onChange: (a: Align) => void }) {
  const opts: { v: Align; label: string }[] = [
    { v: "left", label: "⟸" },
    { v: "center", label: "⇔" },
    { v: "right", label: "⟹" },
  ];
  return (
    <div className="block-meta-row">
      {opts.map((o) => (
        <button
          key={o.v}
          type="button"
          className={`tt-btn ${value === o.v ? "is-active" : ""}`}
          onClick={() => onChange(o.v)}
          title={`Align ${o.v}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* =============================================================================
 * Heading
 * ========================================================================== */

interface HeadingData {
  level: 1 | 2 | 3;
  text: string;
  align: Align;
  /** Optional small italic eyebrow shown above the headline. */
  eyebrow?: string;
}

const HeadingRender = ({ data }: RenderProps<HeadingData>) => {
  const inner = data.text || <em className="text-[var(--color-ink-light)]">Heading</em>;
  const style = { textAlign: data.align } as React.CSSProperties;
  return (
    <div className="vu-heading" style={style}>
      {data.eyebrow && <p className="eyebrow mb-3">{data.eyebrow}</p>}
      {data.level === 1 ? <h1>{inner}</h1> : data.level === 2 ? <h2>{inner}</h2> : <h3>{inner}</h3>}
    </div>
  );
};

const HeadingEdit = ({ data, onChange }: EditProps<HeadingData>) => {
  const Tag = data.level === 1 ? "h1" : data.level === 2 ? "h2" : "h3";
  return (
    <div className="vu-heading" style={{ textAlign: data.align }}>
      <div className="block-meta-row">
        {[1, 2, 3].map((lvl) => (
          <button
            key={lvl}
            type="button"
            className={`tt-btn ${data.level === lvl ? "is-active" : ""}`}
            onClick={() => onChange({ ...data, level: lvl as HeadingData["level"] })}
            title={`Heading ${lvl}`}
          >
            H{lvl}
          </button>
        ))}
        <AlignPicker value={data.align} onChange={(a) => onChange({ ...data, align: a })} />
      </div>
      <input
        type="text"
        value={data.eyebrow ?? ""}
        onChange={(e) => onChange({ ...data, eyebrow: e.target.value })}
        placeholder="Eyebrow (optional)"
        className="block-eyebrow-input"
      />
      <Tag>
        <input
          type="text"
          autoFocus
          value={data.text}
          onChange={(e) => onChange({ ...data, text: e.target.value })}
          placeholder="Heading"
          className="block-heading-input"
          style={{ textAlign: data.align }}
        />
      </Tag>
    </div>
  );
};

export const Heading: BlockType<HeadingData> = {
  type: "heading",
  label: "Heading",
  icon: "H",
  group: "text",
  description: "A section title (H1, H2, H3) with optional eyebrow.",
  defaultData: () => ({ level: 2, text: "", align: "left", eyebrow: "" }),
  Render: HeadingRender,
  Edit: HeadingEdit,
  toHtml: (d) => {
    const style = ` style="text-align:${d.align}"`;
    const eyebrow = d.eyebrow ? `<p class="eyebrow"${style}>${escapeHtml(d.eyebrow)}</p>` : "";
    return `<div class="vu-heading"${style}>${eyebrow}<h${d.level}>${escapeHtml(d.text)}</h${d.level}></div>`;
  },
  toText: (d) => `${d.eyebrow ?? ""} ${d.text}`.trim(),
};

/* =============================================================================
 * Paragraph (rich text)
 * ========================================================================== */

interface ParagraphData {
  html: string;
  align: Align;
  /** "default" / "lead" (larger 1.25rem italic-ish opening) / "drop-cap" (illuminated initial) */
  variant: "default" | "lead" | "drop-cap";
}

const ParagraphRender = ({ data }: RenderProps<ParagraphData>) => {
  if ((!data.html || data.html === "<p></p>") && false) {
    return null;
  }
  const cls = `vu-para vu-para-${data.variant}`;
  const style = { textAlign: data.align } as React.CSSProperties;
  if (!data.html) {
    return <div className={cls} style={style}><p className="text-[var(--color-ink-light)] italic">Paragraph…</p></div>;
  }
  return <div className={cls} style={style} dangerouslySetInnerHTML={{ __html: data.html }} />;
};

const ParagraphEdit = ({ data, onChange }: EditProps<ParagraphData>) => (
  <div className={`vu-para vu-para-${data.variant}`} style={{ textAlign: data.align }}>
    <div className="block-meta-row">
      {(["default", "lead", "drop-cap"] as const).map((v) => (
        <button
          key={v}
          type="button"
          className={`tt-btn ${data.variant === v ? "is-active" : ""}`}
          onClick={() => onChange({ ...data, variant: v })}
        >
          {v}
        </button>
      ))}
      <AlignPicker value={data.align} onChange={(a) => onChange({ ...data, align: a })} />
    </div>
    <InlineRichText
      html={data.html}
      onUpdate={(html) => onChange({ ...data, html })}
      placeholder="Begin writing —"
      autoFocus
      multiline
    />
  </div>
);

export const Paragraph: BlockType<ParagraphData> = {
  type: "paragraph",
  label: "Paragraph",
  icon: "¶",
  group: "text",
  description: "Body text. Lead, drop-cap, alignment options.",
  defaultData: () => ({ html: "", align: "left", variant: "default" }),
  Render: ParagraphRender,
  Edit: ParagraphEdit,
  toHtml: (d) =>
    `<div class="vu-para vu-para-${d.variant}" style="text-align:${d.align}">${d.html || ""}</div>`,
  toText: (d) => stripHtml(d.html),
};

/* =============================================================================
 * Quote
 * ========================================================================== */

interface QuoteData {
  html: string;
  citation: string;
  /** "side" — left-bordered (default), "pull" — large centered editorial pull-quote */
  variant: "side" | "pull";
}

const QuoteRender = ({ data }: RenderProps<QuoteData>) => (
  <blockquote className={`vu-quote vu-quote-${data.variant}`}>
    <div dangerouslySetInnerHTML={{ __html: data.html || "<p></p>" }} />
    {data.citation && <footer>— {data.citation}</footer>}
  </blockquote>
);

const QuoteEdit = ({ data, onChange }: EditProps<QuoteData>) => (
  <blockquote className={`vu-quote vu-quote-${data.variant}`}>
    <div className="block-meta-row">
      {(["side", "pull"] as const).map((v) => (
        <button
          key={v}
          type="button"
          className={`tt-btn ${data.variant === v ? "is-active" : ""}`}
          onClick={() => onChange({ ...data, variant: v })}
        >
          {v}
        </button>
      ))}
    </div>
    <InlineRichText
      html={data.html}
      onUpdate={(html) => onChange({ ...data, html })}
      placeholder="A passage worth setting apart."
      autoFocus
      multiline
    />
    <input
      type="text"
      value={data.citation}
      onChange={(e) => onChange({ ...data, citation: e.target.value })}
      placeholder="Citation (optional)"
      className="block-citation-input"
    />
  </blockquote>
);

export const Quote: BlockType<QuoteData> = {
  type: "quote",
  label: "Quote",
  icon: "❝",
  group: "text",
  description: "Pull-quote or side-bordered citation.",
  defaultData: () => ({ html: "", citation: "", variant: "side" }),
  Render: QuoteRender,
  Edit: QuoteEdit,
  toHtml: (d) =>
    `<blockquote class="vu-quote vu-quote-${d.variant}">${d.html || ""}${d.citation ? `<footer>— ${escapeHtml(d.citation)}</footer>` : ""}</blockquote>`,
  toText: (d) => `${stripHtml(d.html)} ${d.citation}`.trim(),
};

/* =============================================================================
 * Devanagari verse — editorial special: deva, IAST, English, source
 * ========================================================================== */

interface VerseData {
  deva: string;
  iast: string;
  english: string;
  source: string;
}

const VerseRender = ({ data }: RenderProps<VerseData>) => (
  <figure className="vu-verse">
    {data.deva && <p className="vu-verse-deva">{data.deva}</p>}
    {data.iast && <p className="vu-verse-iast">{data.iast}</p>}
    {data.english && (
      <>
        <hr className="vu-verse-rule" />
        <p className="vu-verse-english">"{data.english}"</p>
      </>
    )}
    {data.source && <figcaption className="vu-verse-source">— {data.source}</figcaption>}
  </figure>
);

const VerseEdit = ({ data, onChange }: EditProps<VerseData>) => (
  <figure className="vu-verse">
    <input
      className="vu-verse-input vu-verse-deva"
      placeholder="Devanagari (e.g. सत्यमेव जयते)"
      value={data.deva}
      onChange={(e) => onChange({ ...data, deva: e.target.value })}
      autoFocus
    />
    <input
      className="vu-verse-input vu-verse-iast"
      placeholder="IAST (e.g. Satyam eva jayate)"
      value={data.iast}
      onChange={(e) => onChange({ ...data, iast: e.target.value })}
    />
    <hr className="vu-verse-rule" />
    <input
      className="vu-verse-input vu-verse-english"
      placeholder="English translation"
      value={data.english}
      onChange={(e) => onChange({ ...data, english: e.target.value })}
    />
    <input
      className="vu-verse-input vu-verse-source"
      placeholder="Source (e.g. Muṇḍaka Upaniṣad 3.1.6)"
      value={data.source}
      onChange={(e) => onChange({ ...data, source: e.target.value })}
    />
  </figure>
);

export const Verse: BlockType<VerseData> = {
  type: "verse",
  label: "Vedic verse",
  icon: "ॐ",
  group: "special",
  description: "Devanagari + IAST + English + source. The house style.",
  defaultData: () => ({ deva: "", iast: "", english: "", source: "" }),
  Render: VerseRender,
  Edit: VerseEdit,
  toHtml: (d) =>
    `<figure class="vu-verse">${d.deva ? `<p class="vu-verse-deva">${escapeHtml(d.deva)}</p>` : ""}${d.iast ? `<p class="vu-verse-iast">${escapeHtml(d.iast)}</p>` : ""}${d.english ? `<hr class="vu-verse-rule"/><p class="vu-verse-english">&quot;${escapeHtml(d.english)}&quot;</p>` : ""}${d.source ? `<figcaption class="vu-verse-source">— ${escapeHtml(d.source)}</figcaption>` : ""}</figure>`,
  toText: (d) => `${d.english || d.iast || d.deva} ${d.source}`.trim(),
};

/* =============================================================================
 * Image
 * ========================================================================== */

interface ImageData {
  src: string;
  alt: string;
  caption: string;
  fit: "narrow" | "wide" | "full" | "bleed";
  align: Align;
  frame: "none" | "thin" | "violet";
  rounded: boolean;
}

const ImageRender = ({ data, inEditor }: RenderProps<ImageData>) => {
  if (!data.src && inEditor) {
    return (
      <div className="image-placeholder">
        <span className="font-display-italic text-[var(--color-ink-mute)]">No image yet — click to add one.</span>
      </div>
    );
  }
  if (!data.src) return null;
  const cls = [
    "vu-image",
    `vu-image-${data.fit}`,
    `vu-image-align-${data.align}`,
    `vu-image-frame-${data.frame}`,
    data.rounded ? "is-rounded" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <figure className={cls}>
      <img src={data.src} alt={data.alt} loading="lazy" />
      {data.caption && <figcaption>{data.caption}</figcaption>}
    </figure>
  );
};

const ImageEdit = ({ data, onChange }: EditProps<ImageData>) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const baseCls = [
    "vu-image",
    `vu-image-${data.fit}`,
    `vu-image-align-${data.align}`,
    `vu-image-frame-${data.frame}`,
    data.rounded ? "is-rounded" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <figure className={baseCls}>
      {data.src ? (
        <img src={data.src} alt={data.alt} loading="lazy" />
      ) : (
        <button
          type="button"
          className="image-placeholder image-placeholder-action"
          onClick={() => setPickerOpen(true)}
        >
          <span className="font-grotesk text-[0.9rem] text-[var(--color-ink-soft)]">
            Click to upload an image — or paste a URL.
          </span>
        </button>
      )}
      {data.caption && <figcaption>{data.caption}</figcaption>}
      <div className="block-meta-grid">
        {(pickerOpen || !data.src) && (
          <MediaPicker
            kind="image"
            currentUrl={data.src}
            onPick={(url, meta) => {
              onChange({
                ...data,
                src: url,
                alt: data.alt || meta?.alt || "",
              });
              setPickerOpen(false);
            }}
            onCancel={data.src ? () => setPickerOpen(false) : undefined}
          />
        )}
        {data.src && !pickerOpen && (
          <button type="button" className="tt-btn" onClick={() => setPickerOpen(true)}>
            Replace image
          </button>
        )}
        <input
          type="text"
          placeholder="Alt text (for screen readers)"
          value={data.alt}
          onChange={(e) => onChange({ ...data, alt: e.target.value })}
        />
        <input
          type="text"
          placeholder="Caption (optional)"
          value={data.caption}
          onChange={(e) => onChange({ ...data, caption: e.target.value })}
        />
        <div className="block-meta-row">
          {(["narrow", "wide", "full", "bleed"] as const).map((f) => (
            <button
              key={f}
              type="button"
              className={`tt-btn ${data.fit === f ? "is-active" : ""}`}
              onClick={() => onChange({ ...data, fit: f })}
              title={`Width: ${f}`}
            >
              {f}
            </button>
          ))}
        </div>
        <AlignPicker value={data.align} onChange={(a) => onChange({ ...data, align: a })} />
        <div className="block-meta-row">
          {(["none", "thin", "violet"] as const).map((f) => (
            <button
              key={f}
              type="button"
              className={`tt-btn ${data.frame === f ? "is-active" : ""}`}
              onClick={() => onChange({ ...data, frame: f })}
              title={`Frame: ${f}`}
            >
              {f}
            </button>
          ))}
          <button
            type="button"
            className={`tt-btn ${data.rounded ? "is-active" : ""}`}
            onClick={() => onChange({ ...data, rounded: !data.rounded })}
            title="Rounded corners"
          >
            ⬭
          </button>
        </div>
      </div>
    </figure>
  );
};

export const Image: BlockType<ImageData> = {
  type: "image",
  label: "Image",
  icon: "⬚",
  group: "media",
  description: "Width, alignment, frame, rounded corners.",
  defaultData: () => ({ src: "", alt: "", caption: "", fit: "wide", align: "center", frame: "thin", rounded: false }),
  Render: ImageRender,
  Edit: ImageEdit,
  toHtml: (d) => {
    if (!d.src) return "";
    const cls = ["vu-image", `vu-image-${d.fit}`, `vu-image-align-${d.align}`, `vu-image-frame-${d.frame}`, d.rounded ? "is-rounded" : ""]
      .filter(Boolean)
      .join(" ");
    return `<figure class="${cls}"><img src="${escapeHtml(d.src)}" alt="${escapeHtml(d.alt)}" loading="lazy"/>${d.caption ? `<figcaption>${escapeHtml(d.caption)}</figcaption>` : ""}</figure>`;
  },
  toText: (d) => d.caption || d.alt,
};

/* =============================================================================
 * Gallery — multiple images in a grid
 * ========================================================================== */

interface GalleryItem {
  src: string;
  alt: string;
}
interface GalleryData {
  items: GalleryItem[];
  columns: 2 | 3 | 4;
  caption: string;
}

const GalleryRender = ({ data, inEditor }: RenderProps<GalleryData>) => {
  if (data.items.length === 0 && inEditor) {
    return <div className="image-placeholder"><span className="font-display-italic text-[var(--color-ink-mute)]">Empty gallery — add image URLs.</span></div>;
  }
  if (data.items.length === 0) return null;
  return (
    <figure className="vu-gallery" data-cols={data.columns}>
      <div className="vu-gallery-grid">
        {data.items.map((it, i) => (
          <img key={i} src={it.src} alt={it.alt} loading="lazy" />
        ))}
      </div>
      {data.caption && <figcaption>{data.caption}</figcaption>}
    </figure>
  );
};

const GalleryEdit = ({ data, onChange }: EditProps<GalleryData>) => {
  const update = (i: number, patch: Partial<GalleryItem>) => {
    const items = data.items.slice();
    items[i] = { ...items[i]!, ...patch };
    onChange({ ...data, items });
  };
  const remove = (i: number) => {
    const items = data.items.slice();
    items.splice(i, 1);
    onChange({ ...data, items });
  };
  const add = () => onChange({ ...data, items: [...data.items, { src: "", alt: "" }] });

  return (
    <figure className="vu-gallery" data-cols={data.columns}>
      <div className="vu-gallery-grid">
        {data.items.map((it, i) => (
          <div key={i} className="vu-gallery-cell">
            {it.src && <img src={it.src} alt={it.alt} loading="lazy" />}
            <input
              type="url"
              placeholder="Image URL"
              value={it.src}
              onChange={(e) => update(i, { src: e.target.value })}
            />
            <input
              type="text"
              placeholder="Alt text"
              value={it.alt}
              onChange={(e) => update(i, { alt: e.target.value })}
            />
            <button type="button" className="tt-btn" onClick={() => remove(i)}>Remove</button>
          </div>
        ))}
      </div>
      <div className="block-meta-row">
        <button type="button" className="tt-btn" onClick={add}>+ Image</button>
        {([2, 3, 4] as const).map((c) => (
          <button
            key={c}
            type="button"
            className={`tt-btn ${data.columns === c ? "is-active" : ""}`}
            onClick={() => onChange({ ...data, columns: c })}
          >
            {c} cols
          </button>
        ))}
      </div>
      <input
        type="text"
        placeholder="Gallery caption (optional)"
        value={data.caption}
        onChange={(e) => onChange({ ...data, caption: e.target.value })}
        className="block-citation-input"
      />
    </figure>
  );
};

export const Gallery: BlockType<GalleryData> = {
  type: "gallery",
  label: "Gallery",
  icon: "▦",
  group: "media",
  description: "A grid of images, 2–4 columns.",
  defaultData: () => ({ items: [], columns: 3, caption: "" }),
  Render: GalleryRender,
  Edit: GalleryEdit,
  toHtml: (d) => {
    if (d.items.length === 0) return "";
    const grid = d.items
      .filter((i) => i.src)
      .map((i) => `<img src="${escapeHtml(i.src)}" alt="${escapeHtml(i.alt)}" loading="lazy"/>`)
      .join("");
    return `<figure class="vu-gallery" data-cols="${d.columns}"><div class="vu-gallery-grid">${grid}</div>${d.caption ? `<figcaption>${escapeHtml(d.caption)}</figcaption>` : ""}</figure>`;
  },
  toText: (d) => d.caption,
};

/* =============================================================================
 * Divider
 * ========================================================================== */

interface DividerData {
  variant: "rule" | "violet" | "ornament";
}

const DividerRender = ({ data }: RenderProps<DividerData>) => {
  if (data.variant === "ornament") {
    return <p className="vu-divider-ornament">❦</p>;
  }
  return <hr className={`vu-divider vu-divider-${data.variant}`} />;
};

const DividerEdit = ({ data, onChange }: EditProps<DividerData>) => (
  <div>
    <DividerRender data={data} />
    <div className="block-meta-row">
      {(["rule", "violet", "ornament"] as const).map((v) => (
        <button
          key={v}
          type="button"
          className={`tt-btn ${data.variant === v ? "is-active" : ""}`}
          onClick={() => onChange({ variant: v })}
        >
          {v}
        </button>
      ))}
    </div>
  </div>
);

export const Divider: BlockType<DividerData> = {
  type: "divider",
  label: "Divider",
  icon: "─",
  group: "layout",
  description: "Horizontal rule, violet rule, or a ❦ ornament.",
  defaultData: () => ({ variant: "rule" }),
  Render: DividerRender,
  Edit: DividerEdit,
  toHtml: (d) =>
    d.variant === "ornament"
      ? '<p class="vu-divider-ornament">❦</p>'
      : `<hr class="vu-divider vu-divider-${d.variant}"/>`,
};

/* =============================================================================
 * Callout
 * ========================================================================== */

interface CalloutData {
  variant: "note" | "warning" | "saying" | "insight" | "tip" | "question";
  title: string;
  html: string;
}

const CalloutRender = ({ data }: RenderProps<CalloutData>) => (
  <aside className={`vu-callout vu-callout-${data.variant}`}>
    {data.title && <p className="vu-callout-title">{data.title}</p>}
    <div dangerouslySetInnerHTML={{ __html: data.html }} />
  </aside>
);

const CalloutEdit = ({ data, onChange }: EditProps<CalloutData>) => (
  <aside className={`vu-callout vu-callout-${data.variant}`}>
    <div className="block-meta-row">
      {(["note", "warning", "saying", "insight", "tip", "question"] as const).map((v) => (
        <button
          key={v}
          type="button"
          className={`tt-btn ${data.variant === v ? "is-active" : ""}`}
          onClick={() => onChange({ ...data, variant: v })}
        >
          {v}
        </button>
      ))}
    </div>
    <input
      type="text"
      className="vu-callout-title-input"
      placeholder="Title (optional)"
      value={data.title}
      onChange={(e) => onChange({ ...data, title: e.target.value })}
    />
    <InlineRichText
      html={data.html}
      onUpdate={(html) => onChange({ ...data, html })}
      placeholder="Callout body"
      multiline
    />
  </aside>
);

export const Callout: BlockType<CalloutData> = {
  type: "callout",
  label: "Callout",
  icon: "ℹ",
  group: "text",
  description: "Set-apart panel — note, warning, or saying.",
  defaultData: () => ({ variant: "note", title: "", html: "" }),
  Render: CalloutRender,
  Edit: CalloutEdit,
  toHtml: (d) =>
    `<aside class="vu-callout vu-callout-${d.variant}">${d.title ? `<p class="vu-callout-title">${escapeHtml(d.title)}</p>` : ""}${d.html || ""}</aside>`,
  toText: (d) => `${d.title} ${stripHtml(d.html)}`.trim(),
};

/* =============================================================================
 * Video — YouTube / Vimeo
 * ========================================================================== */

interface VideoData {
  url: string;
  caption: string;
}

function parseVideo(url: string): { provider: "youtube" | "vimeo"; id: string } | null {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu")) {
      const id =
        u.searchParams.get("v") ?? u.pathname.split("/").filter(Boolean).pop() ?? "";
      if (id) return { provider: "youtube", id };
    }
    if (u.hostname.includes("vimeo")) {
      const id = u.pathname.split("/").filter(Boolean).pop() ?? "";
      if (id) return { provider: "vimeo", id };
    }
  } catch {
    // not a URL
  }
  return null;
}

const VideoRender = ({ data, inEditor }: RenderProps<VideoData>) => {
  const parsed = parseVideo(data.url);
  if (!parsed && inEditor) {
    return (
      <div className="image-placeholder">
        <span className="font-display-italic text-[var(--color-ink-mute)]">Paste a YouTube or Vimeo URL.</span>
      </div>
    );
  }
  if (!parsed) return null;
  const src =
    parsed.provider === "youtube"
      ? `https://www.youtube-nocookie.com/embed/${parsed.id}`
      : `https://player.vimeo.com/video/${parsed.id}`;
  return (
    <figure className="vu-video">
      <iframe
        src={src}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        loading="lazy"
        referrerPolicy="strict-origin-when-cross-origin"
        title={data.caption || "Embedded video"}
      />
      {data.caption && <figcaption>{data.caption}</figcaption>}
    </figure>
  );
};

const VideoEdit = ({ data, onChange }: EditProps<VideoData>) => (
  <figure className="vu-video">
    <input
      type="url"
      placeholder="YouTube or Vimeo URL"
      value={data.url}
      onChange={(e) => onChange({ ...data, url: e.target.value })}
      autoFocus
    />
    {parseVideo(data.url) && <VideoRender data={data} inEditor />}
    <input
      type="text"
      placeholder="Caption (optional)"
      value={data.caption}
      onChange={(e) => onChange({ ...data, caption: e.target.value })}
      className="block-citation-input"
    />
  </figure>
);

export const Video: BlockType<VideoData> = {
  type: "video",
  label: "Video",
  icon: "▶",
  group: "embed",
  description: "Embed a YouTube or Vimeo video.",
  defaultData: () => ({ url: "", caption: "" }),
  Render: VideoRender,
  Edit: VideoEdit,
  toHtml: (d) => {
    const parsed = parseVideo(d.url);
    if (!parsed) return "";
    const src =
      parsed.provider === "youtube"
        ? `https://www.youtube-nocookie.com/embed/${parsed.id}`
        : `https://player.vimeo.com/video/${parsed.id}`;
    return `<figure class="vu-video"><iframe src="${src}" allowfullscreen loading="lazy" referrerpolicy="strict-origin-when-cross-origin"></iframe>${d.caption ? `<figcaption>${escapeHtml(d.caption)}</figcaption>` : ""}</figure>`;
  },
  toText: (d) => d.caption,
};

/* =============================================================================
 * Embed — custom HTML (sanitised on save server-side)
 * ========================================================================== */

interface EmbedData {
  html: string;
}

const EmbedRender = ({ data, inEditor }: RenderProps<EmbedData>) => {
  if (!data.html && inEditor) {
    return (
      <div className="image-placeholder">
        <span className="font-display-italic text-[var(--color-ink-mute)]">Custom HTML embed — empty.</span>
      </div>
    );
  }
  return <div className="vu-embed" dangerouslySetInnerHTML={{ __html: data.html }} />;
};

const EmbedEdit = ({ data, onChange }: EditProps<EmbedData>) => (
  <div>
    <EmbedRender data={data} inEditor />
    <textarea
      rows={6}
      className="vu-embed-textarea"
      placeholder="<div>...your HTML...</div>"
      value={data.html}
      onChange={(e) => onChange({ html: e.target.value })}
      autoFocus
    />
    <p className="auth-hint">Saved HTML is sanitised — `&lt;script&gt;`, on-handlers and most iframes are stripped.</p>
  </div>
);

export const Embed: BlockType<EmbedData> = {
  type: "embed",
  label: "Custom HTML",
  icon: "⌨",
  group: "embed",
  description: "Paste raw HTML (sanitised on save).",
  defaultData: () => ({ html: "" }),
  Render: EmbedRender,
  Edit: EmbedEdit,
  toHtml: (d) => d.html || "",
};

/* =============================================================================
 * Chant — embeds the reusable marked-text reader given a marked-text source.
 *
 * `src` is a marked-text JSON; `select` optionally narrows it to PART of that
 * document — a section, some verses, or a range — so a document can carry just
 * the Gaṇeśa stotram, or three verses of it, without a second copy of the text.
 * The reader itself owns its floating settings, script/mark controls, audio and
 * word-grammar; this block just places it in the document flow.
 * ========================================================================== */

interface ChantData {
  src: string;
  title?: string;
  note?: string;
  /** Optional slice of the source — see `ChantSelection` in @vedaunion/shared. */
  select?: ChantSelection;
}

/** "a, b" <-> ["a","b"] for the id-list fields in the editor. */
const idList = (v: string): string[] | undefined => {
  const out = v.split(",").map((x) => x.trim()).filter(Boolean);
  return out.length ? out : undefined;
};
const idText = (v?: string[]): string => (v ?? []).join(", ");

const ChantRender = ({ data, inEditor }: RenderProps<ChantData>) => {
  if (!data.src) {
    return (
      <div className="image-placeholder">
        <span className="font-display-italic text-[var(--color-ink-mute)]">Chant reader — no source selected.</span>
      </div>
    );
  }
  // Inside the editor canvas, show a lightweight placeholder rather than mounting
  // the full interactive reader (its own audio/settings would fight the editor).
  if (inEditor) {
    return (
      <div className="veda-panel">
        <p className="page-eyebrow mb-1">Chant reader</p>
        <p className="font-grotesk text-[0.85rem] text-[var(--color-ink-mute)] break-all">
          {data.title ? data.title + " · " : ""}{data.src}
          {isEmptySelection(data.select) ? "" : " · part"}
        </p>
      </div>
    );
  }
  // Variable slots (the `deity`) are resolved by the reader itself, from the
  // reader's own preferences — see ChantReader.
  return <ChantEmbed src={data.src} title={data.title} note={data.note} select={data.select} />;
};

const ChantEdit = ({ data, onChange }: EditProps<ChantData>) => {
  const sel: ChantSelection = data.select ?? {};
  const setSel = (patch: Partial<ChantSelection>) => {
    const next: ChantSelection = { ...sel, ...patch };
    for (const k of Object.keys(next) as (keyof ChantSelection)[]) {
      const v = next[k];
      if (v === undefined || v === "" || (Array.isArray(v) && !v.length)) delete next[k];
    }
    onChange({ ...data, select: Object.keys(next).length ? next : undefined });
  };
  return (
    <div>
      <ChantRender data={data} inEditor />
      <label className="auth-hint" style={{ display: "block", marginTop: "0.5rem" }}>
        Which chant
      </label>
      <ChantPicker
        value={data.src}
        onPick={(src, title) => onChange({ ...data, src, title: data.title || title })}
      />
      <label className="auth-hint" style={{ display: "block", marginTop: "0.5rem" }}>
        …or a path, for anything not listed
      </label>
      <input
        className="auth-input"
        placeholder="/chants/purusha-suktam.json"
        value={data.src}
        onChange={(e) => onChange({ ...data, src: e.target.value })}
      />
      <label className="auth-hint" style={{ display: "block", marginTop: "0.5rem" }}>Intro note (optional)</label>
      <input
        className="auth-input"
        placeholder="e.g. Follow the marks as you recite."
        value={data.note ?? ""}
        onChange={(e) => onChange({ ...data, note: e.target.value })}
      />
      <label className="auth-hint" style={{ display: "block", marginTop: "0.75rem" }}>
        Embed only part of it (optional) — leave empty for the whole text
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input
          className="auth-input"
          placeholder="sections: id, id"
          value={idText(sel.sections)}
          onChange={(e) => setSel({ sections: idList(e.target.value) })}
        />
        <input
          className="auth-input"
          placeholder="skip sections: id, id"
          value={idText(sel.exclude)}
          onChange={(e) => setSel({ exclude: idList(e.target.value) })}
        />
        <input
          className="auth-input"
          placeholder="from verse id"
          value={sel.from ?? ""}
          onChange={(e) => setSel({ from: e.target.value.trim() || undefined })}
        />
        <input
          className="auth-input"
          placeholder="to verse id"
          value={sel.to ?? ""}
          onChange={(e) => setSel({ to: e.target.value.trim() || undefined })}
        />
      </div>
    </div>
  );
};

export const Chant: BlockType<ChantData> = {
  type: "chant",
  label: "Chant reader",
  icon: "\u0950",
  group: "embed",
  description: "Embed the interactive reader — a whole text, or part of one.",
  defaultData: () => ({ src: "", title: "", note: "" }),
  Render: ChantRender,
  Edit: ChantEdit,
  toHtml: (d) =>
    d.src
      ? `<div class="vu-chant-embed"><p>${escapeHtml(d.note || "Interactive chant reader — enable JavaScript to read along with recitation marks, translation and word-by-word grammar.")}</p></div>`
      : "",
  toText: (d) => d.note ?? "",
};

/* =============================================================================
 * Saṅkalpa — the first VARIABLE module: a marked text composed at render time
 * from the day's pañcāṅga and the reader's own choices, rendered by the same
 * reader as every static chant. Its state lives in the reader's preferences, so
 * the deity chosen here also fills the `deity` slots in this document's mantras.
 * ========================================================================== */

interface SankalpaData {
  /** Which length to open at: simple (daily pūjā) / standard / mahā. */
  level?: "simple" | "standard" | "maha";
  heading?: string;
}

const SankalpaRender = ({ data, inEditor }: RenderProps<SankalpaData>) => {
  if (inEditor) {
    return (
      <div className="veda-panel">
        <p className="page-eyebrow mb-1">Saṅkalpa</p>
        <p className="font-grotesk text-[0.85rem] text-[var(--color-ink-mute)]">
          {data.heading ? data.heading + " · " : ""}opens at the {data.level ?? "simple"} form
        </p>
      </div>
    );
  }
  return <SankalpaModule levelOverride={data.level ?? "simple"} embedded heading={data.heading} />;
};

const SankalpaEdit = ({ data, onChange }: EditProps<SankalpaData>) => (
  <div>
    <SankalpaRender data={data} inEditor />
    <label className="auth-hint" style={{ display: "block", marginTop: "0.5rem" }}>Heading (optional)</label>
    <input
      className="auth-input"
      placeholder="e.g. Saṅkalpaḥ"
      value={data.heading ?? ""}
      onChange={(e) => onChange({ ...data, heading: e.target.value })}
      autoFocus
    />
    <label className="auth-hint" style={{ display: "block", marginTop: "0.5rem" }}>Opens at</label>
    <select
      className="auth-input"
      value={data.level ?? "simple"}
      onChange={(e) => onChange({ ...data, level: e.target.value as SankalpaData["level"] })}
    >
      <option value="simple">Simple — the short form of daily pūjā</option>
      <option value="standard">Full — with the day&rsquo;s pañcāṅga</option>
      <option value="maha">Mahā-saṅkalpa — with the cosmic-time preamble</option>
    </select>
  </div>
);

export const Sankalpa: BlockType<SankalpaData> = {
  type: "sankalpa",
  label: "Saṅkalpa",
  icon: "\u0950",
  group: "embed",
  description: "The statement of intent, composed live from the day's pañcāṅga.",
  defaultData: () => ({ level: "simple", heading: "" }),
  Render: SankalpaRender,
  Edit: SankalpaEdit,
  toHtml: (d) =>
    `<div class="vu-sankalpa-embed"><p>${escapeHtml(
      d.heading || "Saṅkalpa",
    )} — the statement of intent, composed for your day and place. Enable JavaScript to compose it.</p></div>`,
  toText: (d) => d.heading ?? "Saṅkalpa",
};

/* =============================================================================
 * List — bulleted or numbered
 * ========================================================================== */

interface ListData {
  ordered: boolean;
  items: string[]; // each is HTML
}

const ListRender = ({ data }: RenderProps<ListData>) => {
  const Tag = data.ordered ? "ol" : "ul";
  return (
    <Tag>
      {data.items.map((html, i) => (
        <li key={i} dangerouslySetInnerHTML={{ __html: html || "" }} />
      ))}
    </Tag>
  );
};

const ListEdit = ({ data, onChange }: EditProps<ListData>) => {
  const Tag = data.ordered ? "ol" : "ul";
  const update = (i: number, html: string) => {
    const items = data.items.slice();
    items[i] = html;
    onChange({ ...data, items });
  };
  const remove = (i: number) => {
    const items = data.items.slice();
    items.splice(i, 1);
    onChange({ ...data, items: items.length ? items : [""] });
  };
  return (
    <div>
      <div className="block-meta-row">
        <button
          type="button"
          className={`tt-btn ${!data.ordered ? "is-active" : ""}`}
          onClick={() => onChange({ ...data, ordered: false })}
        >
          ●
        </button>
        <button
          type="button"
          className={`tt-btn ${data.ordered ? "is-active" : ""}`}
          onClick={() => onChange({ ...data, ordered: true })}
        >
          1.
        </button>
        <button
          type="button"
          className="tt-btn"
          onClick={() => onChange({ ...data, items: [...data.items, ""] })}
        >
          + Item
        </button>
      </div>
      <Tag>
        {data.items.map((html, i) => (
          <li key={i} className="vu-list-edit-item">
            <InlineRichText
              html={html}
              onUpdate={(h) => update(i, h)}
              placeholder="List item"
            />
            <button type="button" className="tt-btn vu-list-remove" onClick={() => remove(i)}>×</button>
          </li>
        ))}
      </Tag>
    </div>
  );
};

export const List: BlockType<ListData> = {
  type: "list",
  label: "List",
  icon: "•",
  group: "text",
  description: "Bulleted or numbered list.",
  defaultData: () => ({ ordered: false, items: [""] }),
  Render: ListRender,
  Edit: ListEdit,
  toHtml: (d) => {
    const tag = d.ordered ? "ol" : "ul";
    const items = d.items.map((h) => `<li>${h || ""}</li>`).join("");
    return `<${tag}>${items}</${tag}>`;
  },
  toText: (d) => d.items.map(stripHtml).join(", "),
};

/* =============================================================================
 * Code block
 * ========================================================================== */

interface CodeData {
  language: string;
  code: string;
}

const CodeRender = ({ data }: RenderProps<CodeData>) => (
  <pre data-lang={data.language || ""}>
    <code>{data.code}</code>
  </pre>
);

const CodeEdit = ({ data, onChange }: EditProps<CodeData>) => (
  <div>
    <input
      type="text"
      className="vu-code-lang-input"
      placeholder="Language (optional, e.g. ts, js, sh)"
      value={data.language}
      onChange={(e) => onChange({ ...data, language: e.target.value })}
    />
    <textarea
      className="vu-code-textarea"
      rows={6}
      placeholder="Paste code here"
      value={data.code}
      onChange={(e) => onChange({ ...data, code: e.target.value })}
      autoFocus
    />
  </div>
);

export const Code: BlockType<CodeData> = {
  type: "code",
  label: "Code",
  icon: "{ }",
  group: "text",
  description: "Monospace block for code snippets.",
  defaultData: () => ({ language: "", code: "" }),
  Render: CodeRender,
  Edit: CodeEdit,
  toHtml: (d) => `<pre data-lang="${escapeHtml(d.language)}"><code>${escapeHtml(d.code)}</code></pre>`,
  toText: (d) => d.code,
};

/* =============================================================================
 * Spacer
 * ========================================================================== */

interface SpacerData {
  size: "sm" | "md" | "lg";
}

const SpacerRender = ({ data }: RenderProps<SpacerData>) => (
  <div className={`vu-spacer vu-spacer-${data.size}`} aria-hidden />
);
const SpacerEdit = ({ data, onChange }: EditProps<SpacerData>) => (
  <div>
    <SpacerRender data={data} />
    <div className="block-meta-row">
      {(["sm", "md", "lg"] as const).map((s) => (
        <button
          key={s}
          type="button"
          className={`tt-btn ${data.size === s ? "is-active" : ""}`}
          onClick={() => onChange({ size: s })}
        >
          {s}
        </button>
      ))}
    </div>
  </div>
);

export const Spacer: BlockType<SpacerData> = {
  type: "spacer",
  label: "Spacer",
  icon: "↕",
  group: "layout",
  description: "Vertical breathing room.",
  defaultData: () => ({ size: "md" }),
  Render: SpacerRender,
  Edit: SpacerEdit,
  toHtml: (d) => `<div class="vu-spacer vu-spacer-${d.size}"></div>`,
};

/* =============================================================================
 * Button — call-to-action link
 * ========================================================================== */

interface ButtonData {
  label: string;
  url: string;
  variant: "primary" | "ghost";
  align: Align;
}

const ButtonRender = ({ data, inEditor }: RenderProps<ButtonData>) => {
  if (!data.url && !data.label && inEditor) {
    return <div className="image-placeholder"><span className="font-display-italic text-[var(--color-ink-mute)]">Empty button.</span></div>;
  }
  return (
    <p className="vu-button-row" style={{ textAlign: data.align }}>
      <a
        href={data.url || "#"}
        className={data.variant === "primary" ? "btn-primary" : "btn-ghost"}
        target={data.url.startsWith("http") ? "_blank" : undefined}
        rel={data.url.startsWith("http") ? "noopener noreferrer" : undefined}
      >
        <span>{data.label || "Button"}</span>
        <span className="btn-arrow-icon" aria-hidden>→</span>
      </a>
    </p>
  );
};

const ButtonEdit = ({ data, onChange }: EditProps<ButtonData>) => (
  <div>
    <ButtonRender data={data} inEditor />
    <div className="block-meta-grid">
      <input
        type="text"
        placeholder="Label"
        value={data.label}
        onChange={(e) => onChange({ ...data, label: e.target.value })}
        autoFocus
      />
      <input
        type="text"
        placeholder="URL — /path or https://…"
        value={data.url}
        onChange={(e) => onChange({ ...data, url: e.target.value })}
      />
      <div className="block-meta-row">
        {(["primary", "ghost"] as const).map((v) => (
          <button
            key={v}
            type="button"
            className={`tt-btn ${data.variant === v ? "is-active" : ""}`}
            onClick={() => onChange({ ...data, variant: v })}
          >
            {v}
          </button>
        ))}
        <AlignPicker value={data.align} onChange={(a) => onChange({ ...data, align: a })} />
      </div>
    </div>
  </div>
);

export const Button: BlockType<ButtonData> = {
  type: "button",
  label: "Button",
  icon: "▭",
  group: "layout",
  description: "Call-to-action link, primary or ghost.",
  defaultData: () => ({ label: "", url: "", variant: "primary", align: "left" }),
  Render: ButtonRender,
  Edit: ButtonEdit,
  toHtml: (d) => {
    const cls = d.variant === "primary" ? "btn-primary" : "btn-ghost";
    const target = d.url.startsWith("http") ? ' target="_blank" rel="noopener noreferrer"' : "";
    return `<p class="vu-button-row" style="text-align:${d.align}"><a href="${escapeHtml(d.url || "#")}" class="${cls}"${target}><span>${escapeHtml(d.label || "Button")}</span></a></p>`;
  },
  toText: (d) => d.label,
};

/* =============================================================================
 * Hero — full-width cover image with overlaid eyebrow + headline + subtitle
 * ========================================================================== */

interface HeroData {
  image: string;
  eyebrow: string;
  title: string;
  subtitle: string;
  align: Align;
  variant: "overlay-dark" | "overlay-light" | "side-image" | "no-image";
  height: "short" | "medium" | "tall";
}

const HeroRender = ({ data }: RenderProps<HeroData>) => {
  const cls = [
    "vu-hero",
    `vu-hero-${data.variant}`,
    `vu-hero-h-${data.height}`,
    `vu-hero-align-${data.align}`,
  ].join(" ");
  if (data.variant === "side-image") {
    return (
      <section className={cls}>
        {data.image && <div className="vu-hero-side"><img src={data.image} alt="" /></div>}
        <div className="vu-hero-body">
          {data.eyebrow && <p className="eyebrow">{data.eyebrow}</p>}
          {data.title && <h1>{data.title}</h1>}
          {data.subtitle && <p className="vu-hero-subtitle">{data.subtitle}</p>}
        </div>
      </section>
    );
  }
  return (
    <section
      className={cls}
      style={data.image ? { backgroundImage: `url(${data.image})` } : undefined}
    >
      <div className="vu-hero-overlay" />
      <div className="vu-hero-body" style={{ textAlign: data.align }}>
        {data.eyebrow && <p className="eyebrow">{data.eyebrow}</p>}
        {data.title && <h1>{data.title}</h1>}
        {data.subtitle && <p className="vu-hero-subtitle">{data.subtitle}</p>}
      </div>
    </section>
  );
};

const HeroEdit = ({ data, onChange }: EditProps<HeroData>) => (
  <div>
    <HeroRender data={data} />
    <div className="block-meta-grid">
      <input
        type="url"
        placeholder="Cover image URL"
        value={data.image}
        onChange={(e) => onChange({ ...data, image: e.target.value })}
      />
      <input
        type="text"
        placeholder="Eyebrow (optional)"
        value={data.eyebrow}
        onChange={(e) => onChange({ ...data, eyebrow: e.target.value })}
      />
      <input
        type="text"
        placeholder="Title"
        value={data.title}
        onChange={(e) => onChange({ ...data, title: e.target.value })}
        autoFocus
      />
      <input
        type="text"
        placeholder="Subtitle (optional)"
        value={data.subtitle}
        onChange={(e) => onChange({ ...data, subtitle: e.target.value })}
      />
      <div className="block-meta-row">
        {(["overlay-dark", "overlay-light", "side-image", "no-image"] as const).map((v) => (
          <button
            key={v}
            type="button"
            className={`tt-btn ${data.variant === v ? "is-active" : ""}`}
            onClick={() => onChange({ ...data, variant: v })}
          >
            {v}
          </button>
        ))}
      </div>
      <div className="block-meta-row">
        {(["short", "medium", "tall"] as const).map((h) => (
          <button
            key={h}
            type="button"
            className={`tt-btn ${data.height === h ? "is-active" : ""}`}
            onClick={() => onChange({ ...data, height: h })}
          >
            {h}
          </button>
        ))}
        <AlignPicker value={data.align} onChange={(a) => onChange({ ...data, align: a })} />
      </div>
    </div>
  </div>
);

export const Hero: BlockType<HeroData> = {
  type: "hero",
  label: "Hero / cover",
  icon: "⛰",
  group: "layout",
  description: "Full-width cover with overlaid title.",
  defaultData: () => ({
    image: "",
    eyebrow: "",
    title: "",
    subtitle: "",
    align: "center",
    variant: "overlay-dark",
    height: "medium",
  }),
  Render: HeroRender,
  Edit: HeroEdit,
  toHtml: (d) => {
    const cls = ["vu-hero", `vu-hero-${d.variant}`, `vu-hero-h-${d.height}`, `vu-hero-align-${d.align}`].join(" ");
    const inner = `${d.eyebrow ? `<p class="eyebrow">${escapeHtml(d.eyebrow)}</p>` : ""}${d.title ? `<h1>${escapeHtml(d.title)}</h1>` : ""}${d.subtitle ? `<p class="vu-hero-subtitle">${escapeHtml(d.subtitle)}</p>` : ""}`;
    if (d.variant === "side-image") {
      return `<section class="${cls}">${d.image ? `<div class="vu-hero-side"><img src="${escapeHtml(d.image)}" alt=""/></div>` : ""}<div class="vu-hero-body">${inner}</div></section>`;
    }
    const bg = d.image ? ` style="background-image:url('${escapeHtml(d.image)}')"` : "";
    return `<section class="${cls}"${bg}><div class="vu-hero-overlay"></div><div class="vu-hero-body" style="text-align:${d.align}">${inner}</div></section>`;
  },
  toText: (d) => `${d.eyebrow} ${d.title} ${d.subtitle}`.trim(),
};

/* =============================================================================
 * Two-column — left + right content (text or image each side)
 * ========================================================================== */

interface ColumnSide {
  kind: "text" | "image";
  html: string;
  imageSrc: string;
  imageAlt: string;
}
interface TwoColData {
  left: ColumnSide;
  right: ColumnSide;
  ratio: "1-1" | "1-2" | "2-1";
  vAlign: "top" | "center" | "bottom";
}

const SideRender = ({ side }: { side: ColumnSide }) => {
  if (side.kind === "image") {
    return side.imageSrc ? (
      <img src={side.imageSrc} alt={side.imageAlt} loading="lazy" />
    ) : (
      <div className="image-placeholder">
        <span className="font-display-italic text-[var(--color-ink-mute)]">Image side</span>
      </div>
    );
  }
  return <div dangerouslySetInnerHTML={{ __html: side.html || "<p>Text side</p>" }} />;
};

const TwoColRender = ({ data }: RenderProps<TwoColData>) => (
  <div className={`vu-two vu-two-${data.ratio} vu-two-v-${data.vAlign}`}>
    <div className="vu-two-cell"><SideRender side={data.left} /></div>
    <div className="vu-two-cell"><SideRender side={data.right} /></div>
  </div>
);

const SideEdit = ({
  side,
  onChange,
  label,
}: {
  side: ColumnSide;
  onChange: (s: ColumnSide) => void;
  label: string;
}) => (
  <div className="vu-two-edit">
    <p className="auth-label" style={{ marginBottom: "0.4rem" }}>{label}</p>
    <div className="block-meta-row">
      {(["text", "image"] as const).map((k) => (
        <button
          key={k}
          type="button"
          className={`tt-btn ${side.kind === k ? "is-active" : ""}`}
          onClick={() => onChange({ ...side, kind: k })}
        >
          {k}
        </button>
      ))}
    </div>
    {side.kind === "text" ? (
      <InlineRichText
        html={side.html}
        onUpdate={(html) => onChange({ ...side, html })}
        placeholder="Text…"
        multiline
      />
    ) : (
      <>
        <input
          type="url"
          placeholder="Image URL"
          value={side.imageSrc}
          onChange={(e) => onChange({ ...side, imageSrc: e.target.value })}
        />
        <input
          type="text"
          placeholder="Alt text"
          value={side.imageAlt}
          onChange={(e) => onChange({ ...side, imageAlt: e.target.value })}
        />
      </>
    )}
  </div>
);

const TwoColEdit = ({ data, onChange }: EditProps<TwoColData>) => (
  <div>
    <div className="block-meta-row">
      {(["1-1", "1-2", "2-1"] as const).map((r) => (
        <button
          key={r}
          type="button"
          className={`tt-btn ${data.ratio === r ? "is-active" : ""}`}
          onClick={() => onChange({ ...data, ratio: r })}
          title={`Ratio ${r.replace("-", ":")}`}
        >
          {r}
        </button>
      ))}
      {(["top", "center", "bottom"] as const).map((v) => (
        <button
          key={v}
          type="button"
          className={`tt-btn ${data.vAlign === v ? "is-active" : ""}`}
          onClick={() => onChange({ ...data, vAlign: v })}
          title={`Vertical ${v}`}
        >
          {v}
        </button>
      ))}
    </div>
    <div className={`vu-two vu-two-${data.ratio} vu-two-v-${data.vAlign}`}>
      <SideEdit side={data.left} onChange={(s) => onChange({ ...data, left: s })} label="Left" />
      <SideEdit side={data.right} onChange={(s) => onChange({ ...data, right: s })} label="Right" />
    </div>
  </div>
);

function sideToHtml(side: ColumnSide): string {
  if (side.kind === "image") {
    return side.imageSrc
      ? `<img src="${escapeHtml(side.imageSrc)}" alt="${escapeHtml(side.imageAlt)}" loading="lazy"/>`
      : "";
  }
  return side.html || "";
}

export const TwoColumn: BlockType<TwoColData> = {
  type: "two-column",
  label: "Two columns",
  icon: "▮▮",
  group: "layout",
  description: "Side-by-side text or image columns.",
  defaultData: () => ({
    left: { kind: "text", html: "", imageSrc: "", imageAlt: "" },
    right: { kind: "text", html: "", imageSrc: "", imageAlt: "" },
    ratio: "1-1",
    vAlign: "top",
  }),
  Render: TwoColRender,
  Edit: TwoColEdit,
  toHtml: (d) =>
    `<div class="vu-two vu-two-${d.ratio} vu-two-v-${d.vAlign}"><div class="vu-two-cell">${sideToHtml(d.left)}</div><div class="vu-two-cell">${sideToHtml(d.right)}</div></div>`,
  toText: (d) => `${stripHtml(d.left.html)} ${stripHtml(d.right.html)}`.trim(),
};

/* =============================================================================
 * Stat — large number + label, like the landing page stats
 * ========================================================================== */

interface StatGroupItem {
  number: string;
  label: string;
}
interface StatData {
  items: StatGroupItem[];
  variant: "minimal" | "framed";
}

const StatRender = ({ data }: RenderProps<StatData>) => (
  <div className={`vu-stats vu-stats-${data.variant}`}>
    {data.items.map((it, i) => (
      <div key={i} className="vu-stat-item">
        <p className="vu-stat-num">{it.number || "—"}</p>
        <p className="vu-stat-label">{it.label}</p>
      </div>
    ))}
  </div>
);

const StatEdit = ({ data, onChange }: EditProps<StatData>) => {
  const update = (i: number, patch: Partial<StatGroupItem>) => {
    const items = data.items.slice();
    items[i] = { ...items[i]!, ...patch };
    onChange({ ...data, items });
  };
  const remove = (i: number) => {
    const items = data.items.slice();
    items.splice(i, 1);
    onChange({ ...data, items });
  };
  return (
    <div>
      <div className="block-meta-row">
        {(["minimal", "framed"] as const).map((v) => (
          <button
            key={v}
            type="button"
            className={`tt-btn ${data.variant === v ? "is-active" : ""}`}
            onClick={() => onChange({ ...data, variant: v })}
          >
            {v}
          </button>
        ))}
        <button
          type="button"
          className="tt-btn"
          onClick={() => onChange({ ...data, items: [...data.items, { number: "", label: "" }] })}
        >
          + Item
        </button>
      </div>
      <div className={`vu-stats vu-stats-${data.variant}`}>
        {data.items.map((it, i) => (
          <div key={i} className="vu-stat-item vu-stat-edit">
            <input
              type="text"
              className="vu-stat-num-input"
              placeholder="100+"
              value={it.number}
              onChange={(e) => update(i, { number: e.target.value })}
            />
            <input
              type="text"
              className="vu-stat-label-input"
              placeholder="learners"
              value={it.label}
              onChange={(e) => update(i, { label: e.target.value })}
            />
            <button type="button" className="tt-btn" onClick={() => remove(i)}>×</button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const Stat: BlockType<StatData> = {
  type: "stat",
  label: "Stats",
  icon: "№",
  group: "layout",
  description: "Big numbers row — learners, classes, countries.",
  defaultData: () => ({
    items: [
      { number: "", label: "" },
      { number: "", label: "" },
      { number: "", label: "" },
    ],
    variant: "minimal",
  }),
  Render: StatRender,
  Edit: StatEdit,
  toHtml: (d) =>
    `<div class="vu-stats vu-stats-${d.variant}">${d.items
      .map(
        (it) =>
          `<div class="vu-stat-item"><p class="vu-stat-num">${escapeHtml(it.number)}</p><p class="vu-stat-label">${escapeHtml(it.label)}</p></div>`,
      )
      .join("")}</div>`,
  toText: (d) => d.items.map((i) => `${i.number} ${i.label}`).join(" · "),
};

/* =============================================================================
 * CTA — bordered call-to-action panel: title + body + button
 * ========================================================================== */

interface CtaData {
  eyebrow: string;
  title: string;
  body: string;
  buttonLabel: string;
  buttonUrl: string;
  align: Align;
  variant: "vellum" | "ink";
}

const CtaRender = ({ data }: RenderProps<CtaData>) => (
  <aside className={`vu-cta vu-cta-${data.variant}`} style={{ textAlign: data.align }}>
    {data.eyebrow && <p className="eyebrow">{data.eyebrow}</p>}
    {data.title && <h3 className="vu-cta-title">{data.title}</h3>}
    {data.body && <p className="vu-cta-body">{data.body}</p>}
    {data.buttonLabel && (
      <p className="vu-cta-actions">
        <a
          href={data.buttonUrl || "#"}
          className={data.variant === "ink" ? "btn-primary" : "btn-primary"}
          target={data.buttonUrl.startsWith("http") ? "_blank" : undefined}
          rel={data.buttonUrl.startsWith("http") ? "noopener noreferrer" : undefined}
        >
          <span>{data.buttonLabel}</span>
          <span className="btn-arrow-icon" aria-hidden>→</span>
        </a>
      </p>
    )}
  </aside>
);

const CtaEdit = ({ data, onChange }: EditProps<CtaData>) => (
  <div>
    <CtaRender data={data} />
    <div className="block-meta-grid">
      <input
        type="text"
        placeholder="Eyebrow (optional)"
        value={data.eyebrow}
        onChange={(e) => onChange({ ...data, eyebrow: e.target.value })}
      />
      <input
        type="text"
        placeholder="Title"
        value={data.title}
        onChange={(e) => onChange({ ...data, title: e.target.value })}
        autoFocus
      />
      <textarea
        placeholder="Body text"
        rows={2}
        value={data.body}
        onChange={(e) => onChange({ ...data, body: e.target.value })}
      />
      <input
        type="text"
        placeholder="Button label"
        value={data.buttonLabel}
        onChange={(e) => onChange({ ...data, buttonLabel: e.target.value })}
      />
      <input
        type="text"
        placeholder="Button URL"
        value={data.buttonUrl}
        onChange={(e) => onChange({ ...data, buttonUrl: e.target.value })}
      />
      <div className="block-meta-row">
        {(["vellum", "ink"] as const).map((v) => (
          <button
            key={v}
            type="button"
            className={`tt-btn ${data.variant === v ? "is-active" : ""}`}
            onClick={() => onChange({ ...data, variant: v })}
          >
            {v}
          </button>
        ))}
        <AlignPicker value={data.align} onChange={(a) => onChange({ ...data, align: a })} />
      </div>
    </div>
  </div>
);

export const Cta: BlockType<CtaData> = {
  type: "cta",
  label: "Call to action",
  icon: "✦",
  group: "layout",
  description: "Bordered panel with title, body, and a button.",
  defaultData: () => ({
    eyebrow: "",
    title: "",
    body: "",
    buttonLabel: "",
    buttonUrl: "",
    align: "center",
    variant: "vellum",
  }),
  Render: CtaRender,
  Edit: CtaEdit,
  toHtml: (d) => {
    const target = d.buttonUrl.startsWith("http") ? ' target="_blank" rel="noopener noreferrer"' : "";
    return `<aside class="vu-cta vu-cta-${d.variant}" style="text-align:${d.align}">${d.eyebrow ? `<p class="eyebrow">${escapeHtml(d.eyebrow)}</p>` : ""}${d.title ? `<h3 class="vu-cta-title">${escapeHtml(d.title)}</h3>` : ""}${d.body ? `<p class="vu-cta-body">${escapeHtml(d.body)}</p>` : ""}${d.buttonLabel ? `<p class="vu-cta-actions"><a href="${escapeHtml(d.buttonUrl || "#")}" class="btn-primary"${target}><span>${escapeHtml(d.buttonLabel)}</span></a></p>` : ""}</aside>`;
  },
  toText: (d) => `${d.eyebrow} ${d.title} ${d.body}`.trim(),
};

/* =============================================================================
 * Table — simple grid (rows × cols), header row, cell alignment, caption
 * ========================================================================== */

interface TableData {
  rows: string[][]; // Each cell is plain HTML (paragraph fragment).
  hasHeader: boolean;
  caption: string;
  align: Align;
}

const tableDefault = (): TableData => ({
  rows: [
    ["Header 1", "Header 2", "Header 3"],
    ["", "", ""],
    ["", "", ""],
  ],
  hasHeader: true,
  caption: "",
  align: "left",
});

const TableRender = ({ data }: RenderProps<TableData>) => {
  const headerRow = data.hasHeader ? data.rows[0] : null;
  const bodyRows = data.hasHeader ? data.rows.slice(1) : data.rows;
  return (
    <figure className="vu-table" style={{ textAlign: data.align }}>
      <table>
        {headerRow && (
          <thead>
            <tr>
              {headerRow.map((c, i) => (
                <th key={i} dangerouslySetInnerHTML={{ __html: c || "" }} />
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {bodyRows.map((r, ri) => (
            <tr key={ri}>
              {r.map((c, ci) => (
                <td key={ci} dangerouslySetInnerHTML={{ __html: c || "" }} />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {data.caption && <figcaption>{data.caption}</figcaption>}
    </figure>
  );
};

const TableEdit = ({ data, onChange }: EditProps<TableData>) => {
  const updateCell = (r: number, c: number, value: string) => {
    const rows = data.rows.map((row, ri) =>
      ri === r ? row.map((cell, ci) => (ci === c ? value : cell)) : row,
    );
    onChange({ ...data, rows });
  };

  const cols = data.rows[0]?.length ?? 0;

  const addRow = () => {
    onChange({ ...data, rows: [...data.rows, Array.from({ length: cols }, () => "")] });
  };
  const removeRow = (idx: number) => {
    if (data.rows.length <= 1) return;
    onChange({ ...data, rows: data.rows.filter((_, i) => i !== idx) });
  };
  const addCol = () => {
    onChange({ ...data, rows: data.rows.map((row) => [...row, ""]) });
  };
  const removeCol = () => {
    if (cols <= 1) return;
    onChange({ ...data, rows: data.rows.map((row) => row.slice(0, -1)) });
  };

  const headerRow = data.hasHeader ? data.rows[0] : null;
  const bodyStartIdx = data.hasHeader ? 1 : 0;
  const bodyRows = data.hasHeader ? data.rows.slice(1) : data.rows;

  return (
    <figure className="vu-table" style={{ textAlign: data.align }}>
      <table>
        {headerRow && (
          <thead>
            <tr>
              {headerRow.map((c, i) => (
                <th key={i}>
                  <input
                    type="text"
                    className="vu-table-cell-input"
                    value={c}
                    onChange={(e) => updateCell(0, i, e.target.value)}
                    placeholder="Header"
                  />
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {bodyRows.map((row, ri) => (
            <tr key={ri}>
              {row.map((c, ci) => (
                <td key={ci}>
                  <input
                    type="text"
                    className="vu-table-cell-input"
                    value={c}
                    onChange={(e) => updateCell(ri + bodyStartIdx, ci, e.target.value)}
                    placeholder="—"
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="block-meta-row">
        <button
          type="button"
          className={`tt-btn ${data.hasHeader ? "is-active" : ""}`}
          onClick={() => onChange({ ...data, hasHeader: !data.hasHeader })}
        >
          Header row
        </button>
        <button type="button" className="tt-btn" onClick={addRow}>
          + Row
        </button>
        <button
          type="button"
          className="tt-btn"
          onClick={() => removeRow(data.rows.length - 1)}
        >
          − Row
        </button>
        <button type="button" className="tt-btn" onClick={addCol}>
          + Column
        </button>
        <button type="button" className="tt-btn" onClick={removeCol}>
          − Column
        </button>
        <AlignPicker value={data.align} onChange={(a) => onChange({ ...data, align: a })} />
      </div>
      <input
        type="text"
        placeholder="Caption (optional)"
        value={data.caption}
        onChange={(e) => onChange({ ...data, caption: e.target.value })}
        className="block-citation-input"
      />
    </figure>
  );
};

export const Table: BlockType<TableData> = {
  type: "table",
  label: "Table",
  icon: "▦",
  group: "layout",
  description: "A simple data table with optional header and caption.",
  defaultData: tableDefault,
  Render: TableRender,
  Edit: TableEdit,
  toHtml: (d) => {
    const headerRow = d.hasHeader ? d.rows[0] : null;
    const bodyRows = d.hasHeader ? d.rows.slice(1) : d.rows;
    const head = headerRow
      ? `<thead><tr>${headerRow.map((c) => `<th>${c || ""}</th>`).join("")}</tr></thead>`
      : "";
    const body = `<tbody>${bodyRows
      .map((r) => `<tr>${r.map((c) => `<td>${c || ""}</td>`).join("")}</tr>`)
      .join("")}</tbody>`;
    return `<figure class="vu-table" style="text-align:${d.align}"><table>${head}${body}</table>${d.caption ? `<figcaption>${escapeHtml(d.caption)}</figcaption>` : ""}</figure>`;
  },
  toText: (d) => {
    const flat = d.rows.flat().map((c) => stripHtml(c)).filter(Boolean).join(" ");
    return `${flat} ${d.caption}`.trim();
  },
};

/* =============================================================================
 * Accordion — list of { summary, body } items, individually collapsible
 * ========================================================================== */

interface AccordionItem {
  summary: string;
  body: string; // Tiptap HTML
}
interface AccordionData {
  items: AccordionItem[];
  /** When true, opening one closes the others. False = independent. */
  exclusive: boolean;
}

const AccordionRender = ({ data }: RenderProps<AccordionData>) => (
  <div className={`vu-accordion ${data.exclusive ? "is-exclusive" : ""}`}>
    {data.items.map((it, i) => (
      <details key={i} name={data.exclusive ? "vu-acc-group" : undefined}>
        <summary>{it.summary || <em className="text-[var(--color-ink-light)]">Section</em>}</summary>
        <div dangerouslySetInnerHTML={{ __html: it.body || "" }} />
      </details>
    ))}
  </div>
);

const AccordionEdit = ({ data, onChange }: EditProps<AccordionData>) => {
  const updateItem = (i: number, patch: Partial<AccordionItem>) => {
    const items = data.items.slice();
    items[i] = { ...items[i]!, ...patch };
    onChange({ ...data, items });
  };
  const removeItem = (i: number) => {
    onChange({ ...data, items: data.items.filter((_, idx) => idx !== i) });
  };
  const addItem = () => {
    onChange({ ...data, items: [...data.items, { summary: "", body: "" }] });
  };
  return (
    <div className={`vu-accordion ${data.exclusive ? "is-exclusive" : ""}`}>
      {data.items.map((it, i) => (
        <details key={i} open>
          <summary>
            <input
              type="text"
              className="vu-accordion-summary-input"
              placeholder="Section title"
              value={it.summary}
              onChange={(e) => updateItem(i, { summary: e.target.value })}
            />
          </summary>
          <div className="vu-accordion-body">
            <InlineRichText
              html={it.body}
              onUpdate={(html) => updateItem(i, { body: html })}
              placeholder="Section body…"
              multiline
            />
            <button type="button" className="tt-btn" onClick={() => removeItem(i)}>
              Remove section
            </button>
          </div>
        </details>
      ))}
      <div className="block-meta-row">
        <button type="button" className="tt-btn" onClick={addItem}>
          + Section
        </button>
        <button
          type="button"
          className={`tt-btn ${data.exclusive ? "is-active" : ""}`}
          onClick={() => onChange({ ...data, exclusive: !data.exclusive })}
          title="Only one section open at a time"
        >
          Exclusive
        </button>
      </div>
    </div>
  );
};

export const Accordion: BlockType<AccordionData> = {
  type: "accordion",
  label: "Accordion",
  icon: "▽",
  group: "layout",
  description: "Collapsible sections with summaries.",
  defaultData: () => ({
    items: [
      { summary: "First section", body: "<p>Body of the first section.</p>" },
      { summary: "Second section", body: "<p>Body of the second section.</p>" },
    ],
    exclusive: false,
  }),
  Render: AccordionRender,
  Edit: AccordionEdit,
  toHtml: (d) => {
    const items = d.items
      .map(
        (it) =>
          `<details${d.exclusive ? ' name="vu-acc-group"' : ""}><summary>${escapeHtml(it.summary)}</summary><div>${it.body || ""}</div></details>`,
      )
      .join("");
    return `<div class="vu-accordion${d.exclusive ? " is-exclusive" : ""}">${items}</div>`;
  },
  toText: (d) => d.items.map((it) => `${it.summary} ${stripHtml(it.body)}`).join(" ").trim(),
};

/* =============================================================================
 * Columns(N) — 2/3/4 column generalisation. Each side is a list of inline
 * blocks (text or image). One level of nesting only.
 * ========================================================================== */

type ColumnSideKind = "text" | "image";
interface ColumnSide {
  kind: ColumnSideKind;
  html: string; // Tiptap HTML when kind = text
  imageSrc: string;
  imageAlt: string;
}

interface ColumnsData {
  count: 2 | 3 | 4;
  vAlign: "top" | "center" | "bottom";
  sides: ColumnSide[];
}

const emptySide = (): ColumnSide => ({
  kind: "text",
  html: "<p>Column body…</p>",
  imageSrc: "",
  imageAlt: "",
});

const ColumnsRender = ({ data }: RenderProps<ColumnsData>) => (
  <div
    className={`vu-columns vu-columns-${data.count} vu-columns-v-${data.vAlign}`}
  >
    {Array.from({ length: data.count }).map((_, i) => {
      const s = data.sides[i] ?? emptySide();
      return (
        <div key={i} className="vu-columns-cell">
          {s.kind === "image" ? (
            s.imageSrc ? (
              <img src={s.imageSrc} alt={s.imageAlt} loading="lazy" />
            ) : null
          ) : (
            <div dangerouslySetInnerHTML={{ __html: s.html || "" }} />
          )}
        </div>
      );
    })}
  </div>
);

const ColumnsEdit = ({ data, onChange }: EditProps<ColumnsData>) => {
  const updateSide = (i: number, patch: Partial<ColumnSide>) => {
    const sides = data.sides.slice();
    sides[i] = { ...sides[i]!, ...patch };
    onChange({ ...data, sides });
  };

  const setCount = (count: 2 | 3 | 4) => {
    const sides = data.sides.slice();
    while (sides.length < count) sides.push(emptySide());
    onChange({ ...data, count, sides });
  };

  return (
    <div className={`vu-columns vu-columns-${data.count} vu-columns-v-${data.vAlign}`}>
      {Array.from({ length: data.count }).map((_, i) => {
        const s = data.sides[i] ?? emptySide();
        return (
          <div key={i} className="vu-columns-cell">
            <div className="block-meta-row">
              {(["text", "image"] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  className={`tt-btn ${s.kind === k ? "is-active" : ""}`}
                  onClick={() => updateSide(i, { kind: k })}
                >
                  {k}
                </button>
              ))}
            </div>
            {s.kind === "text" ? (
              <InlineRichText
                html={s.html}
                onUpdate={(html) => updateSide(i, { html })}
                placeholder="Column body…"
                multiline
              />
            ) : (
              <>
                {s.imageSrc && <img src={s.imageSrc} alt={s.imageAlt} loading="lazy" />}
                <input
                  type="url"
                  placeholder="Image URL"
                  value={s.imageSrc}
                  onChange={(e) => updateSide(i, { imageSrc: e.target.value })}
                />
                <input
                  type="text"
                  placeholder="Alt text"
                  value={s.imageAlt}
                  onChange={(e) => updateSide(i, { imageAlt: e.target.value })}
                />
              </>
            )}
          </div>
        );
      })}
      <div className="block-meta-row" style={{ gridColumn: "1 / -1" }}>
        {([2, 3, 4] as const).map((c) => (
          <button
            key={c}
            type="button"
            className={`tt-btn ${data.count === c ? "is-active" : ""}`}
            onClick={() => setCount(c)}
          >
            {c} cols
          </button>
        ))}
        {(["top", "center", "bottom"] as const).map((v) => (
          <button
            key={v}
            type="button"
            className={`tt-btn ${data.vAlign === v ? "is-active" : ""}`}
            onClick={() => onChange({ ...data, vAlign: v })}
          >
            {v}
          </button>
        ))}
      </div>
    </div>
  );
};

export const Columns: BlockType<ColumnsData> = {
  type: "columns",
  label: "Columns",
  icon: "▮▮",
  group: "layout",
  description: "Two, three, or four columns of text or images.",
  defaultData: () => ({
    count: 2,
    vAlign: "top",
    sides: [emptySide(), emptySide(), emptySide(), emptySide()],
  }),
  Render: ColumnsRender,
  Edit: ColumnsEdit,
  toHtml: (d) => {
    const cells = Array.from({ length: d.count })
      .map((_, i) => d.sides[i] ?? emptySide())
      .map((s) =>
        s.kind === "image"
          ? s.imageSrc
            ? `<div class="vu-columns-cell"><img src="${escapeHtml(s.imageSrc)}" alt="${escapeHtml(s.imageAlt)}" loading="lazy"/></div>`
            : `<div class="vu-columns-cell"></div>`
          : `<div class="vu-columns-cell">${s.html || ""}</div>`,
      )
      .join("");
    return `<div class="vu-columns vu-columns-${d.count} vu-columns-v-${d.vAlign}">${cells}</div>`;
  },
  toText: (d) =>
    Array.from({ length: d.count })
      .map((_, i) => d.sides[i] ?? emptySide())
      .map((s) => (s.kind === "text" ? stripHtml(s.html) : s.imageAlt))
      .filter(Boolean)
      .join(" "),
};

/* =============================================================================
 * Audio — <audio controls>, uses upload flow
 * ========================================================================== */

interface AudioData {
  src: string;
  caption: string;
  filename: string;
}

const AudioRender = ({ data, inEditor }: RenderProps<AudioData>) => {
  if (!data.src && inEditor) {
    return (
      <div className="image-placeholder">
        <span className="font-display-italic text-[var(--color-ink-mute)]">No audio yet — click to add.</span>
      </div>
    );
  }
  if (!data.src) return null;
  return (
    <figure className="vu-audio">
      <audio controls preload="none" src={data.src} />
      {data.caption && <figcaption>{data.caption}</figcaption>}
    </figure>
  );
};

const AudioEdit = ({ data, onChange }: EditProps<AudioData>) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  return (
    <figure className="vu-audio">
      {data.src ? (
        <audio controls preload="none" src={data.src} />
      ) : (
        <button
          type="button"
          className="image-placeholder image-placeholder-action"
          onClick={() => setPickerOpen(true)}
        >
          <span className="font-grotesk text-[0.9rem] text-[var(--color-ink-soft)]">
            Click to upload an audio file — mp3, ogg, wav.
          </span>
        </button>
      )}
      {data.caption && <figcaption>{data.caption}</figcaption>}
      <div className="block-meta-grid">
        {(pickerOpen || !data.src) && (
          <MediaPicker
            kind="audio"
            currentUrl={data.src}
            onPick={(url, meta) => {
              onChange({
                ...data,
                src: url,
                filename: data.filename || meta?.filename || "",
              });
              setPickerOpen(false);
            }}
            onCancel={data.src ? () => setPickerOpen(false) : undefined}
          />
        )}
        {data.src && !pickerOpen && (
          <button type="button" className="tt-btn" onClick={() => setPickerOpen(true)}>
            Replace audio
          </button>
        )}
        <input
          type="text"
          placeholder="Caption (e.g. talk title, date, speaker)"
          value={data.caption}
          onChange={(e) => onChange({ ...data, caption: e.target.value })}
        />
      </div>
    </figure>
  );
};

export const Audio: BlockType<AudioData> = {
  type: "audio",
  label: "Audio",
  icon: "♪",
  group: "media",
  description: "An audio player with caption.",
  defaultData: () => ({ src: "", caption: "", filename: "" }),
  Render: AudioRender,
  Edit: AudioEdit,
  toHtml: (d) => {
    if (!d.src) return "";
    return `<figure class="vu-audio"><audio controls preload="none" src="${escapeHtml(d.src)}"></audio>${d.caption ? `<figcaption>${escapeHtml(d.caption)}</figcaption>` : ""}</figure>`;
  },
  toText: (d) => `${d.caption} ${d.filename}`.trim(),
};

/* =============================================================================
 * PDF embed — link card with file icon (no iframe; mobile-friendly)
 * ========================================================================== */

interface PdfData {
  src: string;
  title: string;
  filename: string;
  bytes: number | null;
}

const PdfRender = ({ data, inEditor }: RenderProps<PdfData>) => {
  if (!data.src && inEditor) {
    return (
      <div className="image-placeholder">
        <span className="font-display-italic text-[var(--color-ink-mute)]">No PDF yet — click to add.</span>
      </div>
    );
  }
  if (!data.src) return null;
  return (
    <a className="vu-pdf-card" href={data.src} target="_blank" rel="noopener noreferrer">
      <span className="vu-pdf-icon" aria-hidden>
        PDF
      </span>
      <span className="vu-pdf-meta">
        <span className="vu-pdf-title">{data.title || data.filename || "Document"}</span>
        {data.bytes && (
          <span className="vu-pdf-size">{(data.bytes / (1024 * 1024)).toFixed(1)} MB</span>
        )}
      </span>
      <span className="vu-pdf-arrow" aria-hidden>
        ↗
      </span>
    </a>
  );
};

const PdfEdit = ({ data, onChange }: EditProps<PdfData>) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  return (
    <div>
      {data.src ? (
        <PdfRender data={data} inEditor={true} />
      ) : (
        <button
          type="button"
          className="image-placeholder image-placeholder-action"
          onClick={() => setPickerOpen(true)}
        >
          <span className="font-grotesk text-[0.9rem] text-[var(--color-ink-soft)]">
            Click to upload a PDF.
          </span>
        </button>
      )}
      <div className="block-meta-grid">
        {(pickerOpen || !data.src) && (
          <MediaPicker
            kind="pdf"
            currentUrl={data.src}
            onPick={(url, meta) => {
              onChange({
                ...data,
                src: url,
                filename: meta?.filename ?? data.filename,
                bytes: meta?.bytes ?? data.bytes,
              });
              setPickerOpen(false);
            }}
            onCancel={data.src ? () => setPickerOpen(false) : undefined}
          />
        )}
        {data.src && !pickerOpen && (
          <button type="button" className="tt-btn" onClick={() => setPickerOpen(true)}>
            Replace PDF
          </button>
        )}
        <input
          type="text"
          placeholder="Display title (optional)"
          value={data.title}
          onChange={(e) => onChange({ ...data, title: e.target.value })}
        />
      </div>
    </div>
  );
};

export const Pdf: BlockType<PdfData> = {
  type: "pdf",
  label: "PDF",
  icon: "📄",
  group: "media",
  description: "A downloadable PDF rendered as a link card.",
  defaultData: () => ({ src: "", title: "", filename: "", bytes: null }),
  Render: PdfRender,
  Edit: PdfEdit,
  toHtml: (d) => {
    if (!d.src) return "";
    return `<a class="vu-pdf-card" href="${escapeHtml(d.src)}" target="_blank" rel="noopener noreferrer"><span class="vu-pdf-icon">PDF</span><span class="vu-pdf-meta"><span class="vu-pdf-title">${escapeHtml(d.title || d.filename || "Document")}</span>${d.bytes ? `<span class="vu-pdf-size">${(d.bytes / (1024 * 1024)).toFixed(1)} MB</span>` : ""}</span><span class="vu-pdf-arrow">↗</span></a>`;
  },
  toText: (d) => `${d.title} ${d.filename}`.trim(),
};

/* =============================================================================
 * Footnotes — block-level "Notes" list. Inline footnote markers go inside
 * paragraph/quote text via `<sup>n</sup>` (the inline-text editor surfaces
 * them through the bubble menu — see inline-text.tsx superscript). The
 * Footnotes block aggregates and renders them.
 * ========================================================================== */

interface FootnotesData {
  items: { html: string }[];
  title: string;
}

const FootnotesRender = ({ data }: RenderProps<FootnotesData>) => (
  <section className="vu-footnotes">
    <p className="eyebrow">{data.title || "Notes"}</p>
    <ol>
      {data.items.map((it, i) => (
        <li key={i} dangerouslySetInnerHTML={{ __html: it.html || "" }} />
      ))}
    </ol>
  </section>
);

const FootnotesEdit = ({ data, onChange }: EditProps<FootnotesData>) => {
  const updateItem = (i: number, html: string) => {
    const items = data.items.slice();
    items[i] = { html };
    onChange({ ...data, items });
  };
  const addItem = () =>
    onChange({ ...data, items: [...data.items, { html: "<p></p>" }] });
  const removeItem = (i: number) =>
    onChange({ ...data, items: data.items.filter((_, idx) => idx !== i) });
  return (
    <section className="vu-footnotes">
      <input
        type="text"
        className="block-eyebrow-input"
        placeholder="Notes"
        value={data.title}
        onChange={(e) => onChange({ ...data, title: e.target.value })}
      />
      <ol>
        {data.items.map((it, i) => (
          <li key={i}>
            <InlineRichText
              html={it.html}
              onUpdate={(html) => updateItem(i, html)}
              placeholder="Footnote text…"
              multiline
            />
            <button type="button" className="tt-btn" onClick={() => removeItem(i)}>
              Remove
            </button>
          </li>
        ))}
      </ol>
      <div className="block-meta-row">
        <button type="button" className="tt-btn" onClick={addItem}>
          + Note
        </button>
      </div>
    </section>
  );
};

export const Footnotes: BlockType<FootnotesData> = {
  type: "footnotes",
  label: "Footnotes",
  icon: "†",
  group: "text",
  description: "An auto-numbered notes list at the foot of the article.",
  defaultData: () => ({ items: [{ html: "<p>First note.</p>" }], title: "Notes" }),
  Render: FootnotesRender,
  Edit: FootnotesEdit,
  toHtml: (d) =>
    `<section class="vu-footnotes"><p class="eyebrow">${escapeHtml(d.title || "Notes")}</p><ol>${d.items.map((it) => `<li>${it.html || ""}</li>`).join("")}</ol></section>`,
  toText: (d) => d.items.map((it) => stripHtml(it.html)).join(" "),
};

/* =============================================================================
 * Math (KaTeX) — block-level LaTeX. Lazy-imports KaTeX so the dep is only
 * pulled in when an article actually contains math. If the dep isn't
 * installed yet, falls back to showing the raw source in a code-style box —
 * the article still saves and reopens cleanly.
 * ========================================================================== */

interface MathData {
  tex: string;
  display: boolean;
}

// Indirect dynamic-import helper: TS treats this as `Promise<unknown>` (no
// static module resolution), so the build doesn't fail when `katex` isn't
// installed. The user can add KaTeX later via `npm i -w client katex` and
// math blocks will start rendering automatically.
const loadKatex = (): Promise<unknown> => {
  const name = "katex";
  return (new Function("m", "return import(m)") as (m: string) => Promise<unknown>)(name);
};
const loadKatexCss = (): Promise<unknown> => {
  const name = "katex/dist/katex.min.css";
  return (new Function("m", "return import(m)") as (m: string) => Promise<unknown>)(name).catch(
    () => undefined,
  );
};

const MathRender = ({ data }: RenderProps<MathData>) => {
  const { tex, display } = data;
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!tex) {
      setHtml("");
      setError(null);
      return;
    }
    setHtml(null);
    setError(null);
    loadKatex()
      .then(async (mod) => {
        if (cancelled) return;
        try {
          const m = mod as {
            default?: { renderToString?: (t: string, o?: unknown) => string };
            renderToString?: (t: string, o?: unknown) => string;
          };
          const renderToString = m.default?.renderToString ?? m.renderToString;
          if (!renderToString) throw new Error("katex export shape unrecognised");
          const out = renderToString(tex, {
            displayMode: display,
            throwOnError: false,
            output: "html",
          });
          await loadKatexCss();
          if (!cancelled) {
            setHtml(out);
            setError(null);
          }
        } catch (err) {
          if (!cancelled) {
            setHtml(null);
            setError(String(err));
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHtml(null);
          setError("KaTeX not installed. Run `npm install -w client katex`.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tex, display]);

  if (error) {
    return (
      <pre className="vu-math vu-math-error" title={error}>
        {tex}
      </pre>
    );
  }
  if (html === null) {
    return <pre className="vu-math vu-math-loading">{tex}</pre>;
  }
  return (
    <div
      className={`vu-math ${display ? "is-display" : "is-inline"}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

const MathEdit = ({ data, onChange }: EditProps<MathData>) => (
  <div>
    <MathRender data={data} />
    <div className="block-meta-grid">
      <textarea
        className="vu-code-textarea"
        rows={3}
        placeholder="LaTeX — e.g. \int_0^\infty e^{-x^2} dx = \frac{\sqrt{\pi}}{2}"
        value={data.tex}
        onChange={(e) => onChange({ ...data, tex: e.target.value })}
      />
      <div className="block-meta-row">
        <button
          type="button"
          className={`tt-btn ${data.display ? "is-active" : ""}`}
          onClick={() => onChange({ ...data, display: !data.display })}
        >
          {data.display ? "Display" : "Inline"}
        </button>
      </div>
    </div>
  </div>
);

export const Math: BlockType<MathData> = {
  type: "math",
  label: "Math (LaTeX)",
  icon: "∑",
  group: "special",
  description: "Block-level LaTeX. Renders via KaTeX.",
  defaultData: () => ({ tex: "E = mc^2", display: true }),
  Render: MathRender,
  Edit: MathEdit,
  toHtml: (d) =>
    // Server-rendered HTML keeps the raw source for SEO; client lazy-renders.
    `<div class="vu-math${d.display ? " is-display" : " is-inline"}" data-tex="${escapeHtml(d.tex)}"><pre>${escapeHtml(d.tex)}</pre></div>`,
  toText: (d) => d.tex,
};

/* =============================================================================
 * Row — side-by-side container for arbitrary blocks
 *
 * Backs the "+ insert left/right" affordances in the BlockEditor canvas. A
 * row holds 2–4 cells; each cell is itself a regular Block of any registered
 * type, which is dispatched back through the editor's block-type registry
 * via `useBlockTypeContext()`. The row is intentionally one level deep —
 * `sideInsertBlock()` adds a cell to an existing row rather than nesting
 * rows inside rows, so authors can compose horizontal layouts without
 * accidentally creating runaway nesting.
 *
 * In the read view, cells render via the registered Render component. In the
 * edit view, cells render via the registered Edit component with their own
 * focus state — clicking a cell focuses it; the row chrome (ratio + remove
 * cell) sits above the cells when the row itself is focused.
 * ========================================================================== */

interface RowData {
  cells: Block[];
  /** Width ratio. Auto-computed from cell count when unset. */
  ratio?: string;
}

const RowRender = ({ data }: RenderProps<RowData>) => {
  const config = useBlockTypeContext();
  const cells = data.cells ?? [];
  const ratio = data.ratio || `vu-row-${cells.length}`;
  if (!config) return null;
  const types = new Map(config.blockTypes.map((t) => [t.type, t]));
  return (
    <div className={`vu-row ${ratio}`}>
      {cells.map((cell) => {
        const t = types.get(cell.type);
        if (!t) return null;
        const Render = t.Render;
        return (
          <div key={cell.id} className="vu-row-cell">
            <Render data={cell.data} inEditor={false} />
          </div>
        );
      })}
    </div>
  );
};

const RowEdit = ({ data, onChange }: EditProps<RowData>) => {
  const config = useBlockTypeContext();
  const cells = data.cells ?? [];
  const ratio = data.ratio || `vu-row-${cells.length}`;
  const [focusedCell, setFocusedCell] = useState<string | null>(null);

  if (!config) return <RowRender data={data} />;
  const types = new Map(config.blockTypes.map((t) => [t.type, t]));

  const updateCell = (cellId: string, nextData: unknown) => {
    onChange({
      ...data,
      cells: cells.map((c) => (c.id === cellId ? { ...c, data: nextData } : c)),
    });
  };

  const removeCell = (cellId: string) => {
    if (cells.length <= 2) return; // a row with one cell collapses; not supported here
    onChange({ ...data, cells: cells.filter((c) => c.id !== cellId) });
  };

  const addCell = () => {
    if (cells.length >= 4) return;
    // Default new cell: an empty paragraph — the most general blank slate.
    const para = types.get("paragraph");
    if (!para) return;
    onChange({
      ...data,
      cells: [
        ...cells,
        { id: newBlockId(), type: "paragraph", data: para.defaultData() },
      ],
    });
  };

  return (
    <div className={`vu-row ${ratio}`}>
      <div className="block-meta-row vu-row-toolbar">
        {cells.length < 4 && (
          <button type="button" className="tt-btn" onClick={addCell}>
            + Column
          </button>
        )}
        <span className="text-[0.65rem] text-[var(--color-ink-mute)]">{cells.length} columns</span>
      </div>
      {cells.map((cell) => {
        const t = types.get(cell.type);
        if (!t) {
          return (
            <div key={cell.id} className="vu-row-cell vu-row-cell-error">
              <p className="auth-error">— Unknown block type: {cell.type}</p>
            </div>
          );
        }
        const Edit = t.Edit;
        const isFocused = focusedCell === cell.id;
        return (
          <div
            key={cell.id}
            className={`vu-row-cell ${isFocused ? "is-focused" : ""}`}
            onMouseDown={(e) => {
              const target = e.target as HTMLElement;
              if (target.closest(".vu-row-cell-remove, .vu-row-toolbar")) return;
              setFocusedCell(cell.id);
            }}
          >
            <Edit
              data={cell.data}
              onChange={(d: unknown) => updateCell(cell.id, d)}
              isFocused={isFocused}
              onCommit={() => setFocusedCell(null)}
            />
            {cells.length > 2 && (
              <button
                type="button"
                className="vu-row-cell-remove"
                onClick={() => removeCell(cell.id)}
                aria-label="Remove column"
                title="Remove this column"
              >
                ×
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export const Row: BlockType<RowData> = {
  type: "row",
  label: "Row",
  icon: "▮▮",
  group: "layout",
  description: "Side-by-side blocks. Created automatically when you click + to the left or right of a block.",
  // The default row holds two empty paragraphs; users normally never insert
  // a Row from the palette (the side-insert flow does it for them) but it's
  // useful to have a sensible default if someone does.
  defaultData: () => ({
    cells: [
      makeBlock("paragraph", { html: "<p>Left column.</p>", align: "left", variant: "default" }),
      makeBlock("paragraph", { html: "<p>Right column.</p>", align: "left", variant: "default" }),
    ],
  }),
  Render: RowRender,
  Edit: RowEdit,
  toHtml: (d) => {
    // Inline server-rendering: walk cells using a static lookup table built
    // from the blocks exported in this file. This avoids importing the
    // articleConfig (circular). For unknown types we emit nothing.
    const cells = d.cells ?? [];
    const cellsHtml = cells
      .map((cell) => {
        const inner = serialiseCell(cell);
        return `<div class="vu-row-cell">${inner}</div>`;
      })
      .join("");
    const ratio = d.ratio || `vu-row-${cells.length}`;
    return `<div class="vu-row ${ratio}">${cellsHtml}</div>`;
  },
  toText: (d) => {
    const cells = d.cells ?? [];
    return cells
      .map((cell) => {
        const fn = textCellLookup.get(cell.type);
        return fn ? fn(cell.data) : "";
      })
      .filter(Boolean)
      .join(" ");
  },
};

/* =============================================================================
 * Row helpers — small lookup maps indirectly populated AFTER all block types
 * are declared. Keeps Row's serialisation self-contained without forcing it
 * to import the registered config (which would create an import cycle).
 * ========================================================================== */

const htmlCellLookup = new Map<string, (data: unknown) => string>();
const textCellLookup = new Map<string, (data: unknown) => string>();

function serialiseCell(cell: Block): string {
  const fn = htmlCellLookup.get(cell.type);
  return fn ? fn(cell.data) : "";
}

/** Called once by `articleConfig.ts` after every block type is in scope. */
export function registerRowSerialisers(types: BlockType[]): void {
  htmlCellLookup.clear();
  textCellLookup.clear();
  for (const t of types) {
    if (t.type === "row") continue; // never recurse Row inside Row
    htmlCellLookup.set(t.type, t.toHtml as (data: unknown) => string);
    if (t.toText) textCellLookup.set(t.type, t.toText as (data: unknown) => string);
  }
}

/* =============================================================================
 * Helper
 * ========================================================================== */

function stripHtml(html: string): string {
  return String(html ?? "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&[a-z]+;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Force `useState` import to be considered used (some block edits use local UI state in future)
void useState;
